/**
 * 需要想定 daily 画面の gate 決定の純関数 (アーキ改善 C3)。
 *
 * 従来 index.tsx の render body に inline 展開されていた gate 群
 * (`canRescale` / `canImportToDraft` / `canImportForecast` /
 * `isRetailerEditableTarget` / `komaBaselineSlots` / 「調整」disabled 理由 /
 * 現在ピーク / BG_TOTAL fallback) の決定を、React mount 無しで unit-addressable な
 * 純関数へ集約する。`useForecastPageModel` がこの関数の唯一の呼び出し口で、
 * index は結果の flag を消費するだけになる。
 *
 * 過去日 write-gate の低レベル判定 (`canMutate`) と write-seam の強制は
 * hook 側 (`useForecastPageModel` の `guardWrite`) が持つ。本 module は
 * `canMutateTargetDate` を boolean 入力として受け取り表示 gate の合成に使うだけで、
 * 実時刻再評価を要する write 判定は行わない (責務分離)。
 */
import { findUnfilledBreakdowns } from '~/pages/DemandForecastDaily/models/aggregateEdit'
import {
  bgTotalBreakdownSlots,
  calcBgTotalSlots,
  computePeak,
} from '~/pages/DemandForecastDaily/models/slotHelpers'
import {
  type AdjustmentDisabledReasonKey,
  adjustmentDisabledReasonKey,
} from '~/pages/DemandForecastDaily/models/toolbarGuards'
import type {
  KomaSlots,
  RetailerEditRow,
  SaveState,
  TargetScope,
} from '~/pages/DemandForecastDaily/models/types'

/** BG 全体ビューでの配下小売数に応じた scope 派生 (AC-DFD-004c/004d)。 */
export type RetailerScope = {
  /** 配下小売が 1 件のみのときの retailerBusinessCd (BG 合計 = 唯一の小売値)。 */
  singleRetailerBusinessCd: string | undefined
  /** BG 全体ビュー + 配下 1 件 → 直接編集可 (isReadOnly 解除 + 各種 gate 通過)。 */
  isSingleRetailerBg: boolean
  /** 配下小売が複数件か (BG 全体ビューでは集計表示の直接編集 AC-DFD-004d / 054 の対象)。 */
  hasMultipleRetailers: boolean
}

/**
 * targetScope と配下 retailers から表示用の scope を導く (AC-DFD-004c/004d)。
 */
export function deriveRetailerScope(
  targetScope: TargetScope,
  retailers: ReadonlyArray<{ retailerBusinessCd: string }>,
): RetailerScope {
  const singleRetailerBusinessCd =
    retailers.length === 1 ? retailers[0]?.retailerBusinessCd : undefined
  const isSingleRetailerBg =
    targetScope === 'BG_TOTAL' && singleRetailerBusinessCd !== undefined
  const hasMultipleRetailers = retailers.length > 1
  return { singleRetailerBusinessCd, isSingleRetailerBg, hasMultipleRetailers }
}

/**
 * BG にグループ別の小売があるか (backend が `byGroup` を付けた小売)。BG 全体からの
 * 取込・採用・トレンド反映は止める。BG 全体の按分編集は内訳の入力・帰属不明残量で判定する。
 */
export function hasGroupForecastRetailer(
  retailers: ReadonlyArray<{ byGroup?: unknown }>,
): boolean {
  return retailers.some((r) => r.byGroup !== undefined)
}

/**
 * 取込系の操作 (簡易予測・確定履歴・参照の採用・トレンド反映) を、グループ別の小売の
 * ために止める理由。
 * - `retailer`: 選んだ小売がグループ別 (葉のタブ・合計タブとも)。小売単位の取込は小売の行に
 *   書くので使えない
 * - `bg`: BG にグループ別の小売がある BG 全体の表示。BG 全体からの取込・採用・トレンド反映は
 *   小売の行へ直接書けないため使えない
 */
export type GroupForecastImportBlock = 'retailer' | 'bg'

export function groupForecastImportBlock(
  targetScope: TargetScope,
  bgHasGroupForecast: boolean,
  isGroupForecastRetailer: boolean,
): GroupForecastImportBlock | undefined {
  if (targetScope === 'RETAILER' && isGroupForecastRetailer) return 'retailer'
  if (targetScope === 'BG_TOTAL' && bgHasGroupForecast) return 'bg'
  return undefined
}

export function deriveAggregateEdit(
  targetScope: TargetScope,
  hasMultipleRetailers: boolean,
  hasUnattributedRemaining: boolean,
  editRows: Record<string, RetailerEditRow>,
  gcKoma: number,
  _bgHasGroupForecast = false,
): { unfilledRetailerNames: string[]; canEdit: boolean } {
  const rows =
    targetScope === 'BG_TOTAL' && hasMultipleRetailers
      ? Object.values(editRows)
      : []
  const unfilledRetailerNames = findUnfilledBreakdowns(rows, gcKoma).map(
    (row) => row.displayName,
  )
  return {
    unfilledRetailerNames,
    canEdit:
      rows.length > 1 &&
      unfilledRetailerNames.length === 0 &&
      !hasUnattributedRemaining,
  }
}

export type ForecastGates = {
  /** 小売編集対象か (共通仕様 AC-DFC-EDIT-001、design L570 + AC-004c)。 */
  isRetailerEditableTarget: boolean
  /**
   * 「今この断面が値変更を許すか」の時相 gate (= `canMutateTargetDate && !isCompleted`)。
   * 小売 scope / 選択行に依存しない。従来 index.tsx / KomaCard に散在していた
   * `canMutateTargetDate && !isCompleted` の手動再 AND (保存ボタン / 確定バー /
   * 実績トレンド反映 / BG 複数小売インフォメッセージ) の単一 SoT。canDiscard だけは
   * 過去日でも残す非対称のためこれを使わない (下記 canDiscard 参照)。
   */
  canEditNow: boolean
  /**
   * BG 全体ビュー + 配下複数で、全内訳の 48 コマに需要想定値が入っている
   * (= 集計値の変更を内訳の現在値の比率で按分できる) か (AC-DFD-054)。true の時は
   * 直接入力・グラフからの需要変更・最大電力補正を集計表示から行える。
   */
  canEditAggregate: boolean
  /**
   * BG 全体ビュー + 配下複数で未入力 (空のコマが 1 つでもある) の内訳名
   * (AC-DFD-004d)。1 件以上ある間は集計表示の直接編集・保存・確定を止め、
   * 「{内訳名}が未入力のため編集できません」を出す。それ以外のビューでは空配列。
   */
  aggregateUnfilledRetailerNames: string[]
  /**
   * BG 全体の未入力内訳のため集計表示の編集・保存・確定を止めているか (AC-DFD-004d)。
   * 小売の葉を保存する操作は止めない。
   */
  isAggregateLocked: boolean
  /**
   * KomaGrid セル / チャート / 参照列採用の read-only 基底 (design L570 + AC-004c、
   * spec AC-DFC-007、AC-DFD-054)。= `!(isRetailerEditableTarget || canEditAggregate) || !canEditNow`。従来 KomaCard が
   * raw 入力から再計算していた `(BG_TOTAL && !single) || isCompleted ||
   * !canMutateTargetDate` を gate 側の単一 SoT にする (KomaCard は本 flag を消費)。
   */
  isReadOnly: boolean
  /** 「最大電力補正 (調整)」活性化条件 (共通仕様 AC-DFC-EDIT-001)。 */
  canRescale: boolean
  /**
   * 「変更を破棄」活性化条件 (共通仕様 AC-DFC-EDIT-002): 小売編集対象 (RETAILER
   * or 配下1件BG) かつ選択行あり・未完了・現在行が dirty のとき true。dirty の
   * 判定源は単一 seam (`useForecastDirtyState` の currentRowDirty = `isRowDirty`)
   * で、KomaCard 側の再計算をやめてこの gate に一本化する。
   */
  canDiscard: boolean
  /** KomaGrid セル / チャートドラッグ編集の可否 (design L570 + AC-004c)。 */
  canImportToDraft: boolean
  /** §5 確定履歴から取込ボタンの活性条件 (AC-08)。 */
  canImportForecast: boolean
  /** KomaGrid の差列 SoT (spec AC-DFD-001)。RETAILER は選択小売の baseline、
   *  BG_TOTAL は Σ小売 baseline (calcBgTotalSlots)。 */
  komaBaselineSlots: KomaSlots | undefined
  /** 「調整」disabled 時の理由キー (優先: 確定済み > 過去日 > 全コマ GC 済み > 小売未選択)。 */
  adjustmentDisabledReasonKey: AdjustmentDisabledReasonKey | undefined
  /** RescaleModal に表示する現在ピーク (kWh 整数、AC-DFC-011)。 */
  currentPeakKwh: number
  /** 取込系の操作をグループ別の小売のために止めている理由 (止めていなければ undefined)。 */
  groupImportBlock: GroupForecastImportBlock | undefined
  /** 変更コマの視覚区別に使う dirtyKomas。BG_TOTAL(複数小売)ビューは
   *  currentEditRow が undefined になるため bgTotalDirtyKomas へ fallback (AC-039)。 */
  effectiveDirtyKomas: Set<number>
  /** 衝突通知・再読込導線の表示に使う saveState。BG_TOTAL(複数小売)ビューは
   *  currentSaveState が undefined になるため anyConflict へ fallback (AC-09)。 */
  effectiveSaveState: SaveState | undefined
}

export type ForecastGatesInput = {
  targetScope: TargetScope
  /** A non-zero scope=UNATTRIBUTED slot exists (all 48 slots are relevant). */
  hasUnattributedRemaining: boolean
  isRetailerEditableTarget: boolean
  aggregateEdit: { unfilledRetailerNames: string[]; canEdit: boolean }
  /** 保存済みの帰属不明内訳。0/未入力は全小売未入力の意味を変えない。 */
  aggregateBaselineSlots?: KomaSlots
  unattributedSlots?: KomaSlots
  unattributedBaselineSlots?: KomaSlots
  /** editor 出力: Σ内訳のライブ合計 (集計表示の最大電力補正の現在ピーク)。 */
  bgTotalLiveSlots: KomaSlots
  /** editor 出力: 選択中小売の編集行 (BG_TOTAL 複数小売ビューでは undefined)。 */
  currentEditRow: RetailerEditRow | undefined
  editRows: Record<string, RetailerEditRow>
  /** read-only (需要想定確定 / 完了 / 削除済み)。 */
  isCompleted: boolean
  /** 過去日 write-gate の表示側 flag (SoT: `canMutate`、hook が実時刻で評価)。 */
  canMutateTargetDate: boolean
  /** BG にグループ別の小売がある (`hasGroupForecastRetailer`)。 */
  bgHasGroupForecast: boolean
  /** 選んだ小売がグループ別 (入力するのは葉の行。合計タブでは入力する行が無い)。 */
  isGroupForecastRetailer: boolean
  gcKoma: number
  /** editor 出力: 選択中小売の dirty コマ集合 (BG_TOTAL 時は空集合)。 */
  currentDirtyKomas: Set<number>
  /**
   * editor 出力: 選択中小売の編集行が dirty か (`isRowDirty` の full 判定、
   * BG_TOTAL 時は false)。canDiscard の判定源。
   */
  currentRowDirty: boolean
  /** editor 出力: 全 editRows の dirtyKomas 和集合 (AC-039 前段)。 */
  bgTotalDirtyKomas: Set<number>
  /** editor 出力: 小売と帰属不明を含む未保存変更の有無。 */
  anyDirty: boolean
  /** editor 出力: 選択中小売の saveState (BG_TOTAL 時は undefined)。 */
  currentSaveState: SaveState | undefined
  /** editor 出力: 1 件でも conflict な小売があるか (AC-09 fallback)。 */
  anyConflict: boolean
  /**
   * editor 出力: 現在表示している active 行に含まれない小売の conflict があるか。
   * 帰属不明の conflict は専用 scope で扱うため含めない。
   */
  hasInactiveConflict?: boolean
}

/**
 * 編集 gate 群を導出する (editor 出力に依存するため editor 生成後に呼ぶ)。
 * 各 gate の意味論・出典コメントは旧 index.tsx の該当箇所を集約したもので、
 * 挙動は不変。
 */
export function deriveForecastGates(input: ForecastGatesInput): ForecastGates {
  const {
    targetScope,
    hasUnattributedRemaining,
    isRetailerEditableTarget,
    aggregateEdit,
    unattributedSlots,
    aggregateBaselineSlots,
    unattributedBaselineSlots,
    bgTotalLiveSlots,
    currentEditRow,
    editRows,
    isCompleted,
    canMutateTargetDate,
    bgHasGroupForecast,
    isGroupForecastRetailer,
    gcKoma,
    currentDirtyKomas,
    currentRowDirty,
    bgTotalDirtyKomas,
    anyDirty,
    currentSaveState,
    anyConflict,
    hasInactiveConflict = false,
  } = input

  const hasCurrentEditRow = currentEditRow !== undefined

  // 時相 gate: 過去日でなく (canMutateTargetDate) 未確定 (!isCompleted) のとき編集可。
  // 散在していた `canMutateTargetDate && !isCompleted` の再 AND を集約する (型 doc 参照)。
  const canEditNow =
    targetScope !== 'UNATTRIBUTED' && canMutateTargetDate && !isCompleted

  // AC-DFD-004d / 054: BG 全体 + 配下複数の集計表示。全内訳の 48 コマに値があれば
  // 集計値の変更を内訳の現在値の比率で按分できるので直接編集を許し、1 つでも
  // 未入力の内訳があれば止める (内訳名を理由に出す)。
  const aggregateUnfilledRetailerNames = aggregateEdit.unfilledRetailerNames
  const isAggregateLocked =
    targetScope === 'BG_TOTAL' && aggregateUnfilledRetailerNames.length > 0
  const canEditAggregate = aggregateEdit.canEdit
  // 書込先 (選択中の小売の行、または集計表示で按分する全内訳) が決まっているか。
  // 編集操作・理由キーはこの 1 つの値から作る。
  const hasEditTarget =
    (isRetailerEditableTarget && hasCurrentEditRow) || canEditAggregate

  // グループ別の小売の合計タブ。葉の足し算を読むだけで、入力する行が無い (DE Q1=A)。
  const isGroupTotal =
    targetScope === 'RETAILER' && isGroupForecastRetailer && !hasCurrentEditRow
  const groupImportBlock = groupForecastImportBlock(
    targetScope,
    bgHasGroupForecast,
    isGroupForecastRetailer,
  )

  // read-only 基底 (KomaCard の raw 再計算をここへ集約): 編集対象 scope でない、
  // 今編集不可 (過去日 / 確定済み)、またはグループ別の小売の合計タブのとき true。
  const isReadOnly =
    !(isRetailerEditableTarget || canEditAggregate) ||
    !canEditNow ||
    (isGroupTotal && !canEditAggregate)

  // canRescale と canImportToDraft は現状同一条件だが、意味 (調整活性 /
  // セル・ドラッグ編集活性) が分岐しうるため別 flag で保持する (旧 index 踏襲)。
  const editEnabled = hasEditTarget && canEditNow
  // 全コマ GC 済みは動かせるコマが無いため最大電力補正を押せなくする
  // (AC-DFC-EDIT-001。#10963 追加 7)。セル編集・ドラッグは別の gate のまま。
  const isAllKomaGcLocked = gcKoma >= 48
  const canRescale = editEnabled && !isAllKomaGcLocked
  const canImportToDraft = editEnabled

  // 「変更を破棄」活性化条件 (共通仕様 AC-DFC-EDIT-002)。過去日 gate
  // (canMutateTargetDate) は意図的に入れない: 通常経路では過去日=編集不可で
  // dirty にならないが、当日ビュー編集中に JST 日跨ぎで過去日化した場合に残った
  // dirty を捨てる唯一の手段として破棄経路は維持する (旧 KomaCard.canDiscard の
  // コメント参照。GC ロックは保存経路の話で、dirty の破棄は逆に乖離を解消する
  // 操作)。dirty は単一 seam の currentRowDirty を消費し KomaCard 側で再計算しない。
  // この非対称のため canEditNow (canMutateTargetDate を含む) ではなく !isCompleted を
  // 直接 AND する — canEditNow に差し替えると過去日で破棄できなくなり退行する。
  // 集計表示 (AC-DFD-054) では、按分で書き換わった内訳のどれかが dirty なら破棄できる
  // (破棄は全内訳を baseline へ戻す)。
  // 帰属不明残量や未入力で直接編集を止めても、既存の小売変更は破棄できる。
  const editTargetDirty =
    targetScope === 'BG_TOTAL' ? anyDirty : currentRowDirty
  const canDiscard =
    (targetScope === 'BG_TOTAL' ||
      (targetScope === 'RETAILER' && (hasCurrentEditRow || isGroupTotal))) &&
    !isCompleted &&
    editTargetDirty

  // §5 取込: 小売ビューは編集可のとき、BG 全体ビューは editRows があれば活性
  // (旧: R1 按分基底ブロックは ADR で解除済み、TC-C-30.3)。
  const canImportForecast =
    targetScope !== 'UNATTRIBUTED' &&
    canEditNow &&
    groupImportBlock === undefined &&
    (targetScope === 'BG_TOTAL'
      ? Object.keys(editRows).length > 0
      : hasCurrentEditRow)

  const komaBaselineSlots =
    aggregateBaselineSlots ??
    (targetScope === 'RETAILER' && currentEditRow !== undefined
      ? currentEditRow.baseline.forecastSlots
      : targetScope === 'BG_TOTAL'
        ? calcBgTotalSlots(
            bgTotalBreakdownSlots(
              Object.values(editRows).map((row) => row.baseline.forecastSlots),
              unattributedBaselineSlots ?? unattributedSlots,
            ),
          )
        : undefined)

  const baseReasonKey = adjustmentDisabledReasonKey({
    canMutateTargetDate,
    isCompleted,
    isAllKomaGcLocked,
    isRetailerEditableTarget: isRetailerEditableTarget || canEditAggregate,
    hasCurrentEditRow: hasEditTarget,
  })

  const groupAggregateBlockReason =
    baseReasonKey === 'noRetailer' && isGroupTotal && !canEditAggregate
      ? aggregateUnfilledRetailerNames.length > 0
        ? 'noRetailer'
        : hasUnattributedRemaining
          ? 'unattributedRemaining'
          : undefined
      : undefined
  const reasonKey =
    groupAggregateBlockReason ??
    (baseReasonKey === 'noRetailer' && isGroupTotal
      ? 'groupTotal'
      : targetScope === 'BG_TOTAL' &&
          hasUnattributedRemaining &&
          baseReasonKey === 'noRetailer'
        ? 'unattributedRemaining'
        : baseReasonKey === 'noRetailer' && groupImportBlock === 'bg'
          ? 'groupForecastBg'
          : baseReasonKey)

  // 集計表示の最大電力補正は集計値 (Σ内訳) のピークを基準にする (AC-DFD-054)。
  const currentPeakKwh =
    currentEditRow !== undefined
      ? computePeak(currentEditRow.forecastSlots, gcKoma)
      : canEditAggregate
        ? computePeak(bgTotalLiveSlots, gcKoma)
        : 0

  const effectiveDirtyKomas =
    currentEditRow !== undefined || isGroupTotal
      ? currentDirtyKomas
      : bgTotalDirtyKomas
  const effectiveSaveState =
    (targetScope === 'BG_TOTAL' && anyConflict) ||
    (targetScope === 'RETAILER' && hasInactiveConflict)
      ? 'conflict'
      : currentEditRow !== undefined || targetScope === 'RETAILER'
        ? currentSaveState
        : anyConflict
          ? 'conflict'
          : undefined

  return {
    isRetailerEditableTarget,
    canEditNow,
    canEditAggregate,
    aggregateUnfilledRetailerNames,
    isAggregateLocked,
    isReadOnly,
    canRescale,
    canDiscard,
    canImportToDraft,
    canImportForecast,
    komaBaselineSlots,
    adjustmentDisabledReasonKey: reasonKey,
    currentPeakKwh,
    groupImportBlock,
    effectiveDirtyKomas,
    effectiveSaveState,
  }
}
